import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Map as MapIcon } from 'lucide-react';
import type { SitemapPageToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useSitemapPagesList, useSitemapPageMutations } from '~/hooks/use-sitemap-pages';
import { tokenToForm, type SitemapPageFormValues } from '~/lib/schemas/sitemap-page';
import { SitemapPageEditDialog } from './sitemap-page-edit-dialog';

/** Sitemap pages CRUD surface — parity with the legacy admin grid (Code/URL). */
export function SitemapPagesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useSitemapPagesList();
	const { remove } = useSitemapPageMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<SitemapPageFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<SitemapPageToken | null>(null);

	const columns = useMemo<SystemGridColumn<SitemapPageToken>[]>(
		() => [
			{
				key: 'code',
				header: t('system.sitemapPages.columns.code'),
				width: '280px',
				sortValue: s => s.code,
				exportValue: s => s.code,
				render: s => <span className="truncate font-mono text-xs font-medium text-foreground">{s.code}</span>
			},
			{
				key: 'url',
				header: t('system.sitemapPages.columns.url'),
				width: 'minmax(0,1fr)',
				sortValue: s => s.url,
				exportValue: s => s.url,
				render: s => (
					<span className="truncate text-sm text-foreground-muted" title={s.url}>
						{s.url}
					</span>
				)
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<SitemapPageToken>
				gridId="sitemap-pages"
				title={t('system.sitemapPages.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={s => s.id ?? s.code}
				columns={columns}
				searchPredicate={(s, q) => `${s.code ?? ''} ${s.url ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'code', dir: 'asc' }}
				emptyIcon={<MapIcon size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.sitemapPages.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={s => {
					setEditing(tokenToForm(s));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="sitemap-pages"
			/>

			<SitemapPageEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.sitemapPages.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.sitemapPages.deleteBody', { name: deleteRow?.code ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
